import {WalletContext} from '#/contexts/wallet-provider';
import {tokenConstants} from '#/data/token-constants';
import {useExchangeRates} from '#/hooks/query/use-exchange-rates';
import {useUserProfile} from '#/hooks/query/use-user-profile';
import {DefaultChildren} from '#/utils/utils';
import {FunctionComponent, createContext, useContext, useMemo} from 'react';

export type CoinDisplayPreferences = {
  symbol: string;
  displayAmount: (usdcAmount: number) => number;
  numericDisplayAmountToUsdc: (amount: number) => number;
  usdcToNumericDisplayAmount: (amount: number) => number;
  usdcToNumericDisplayAmountWithUsdExchangeRate: (amount: number, exchangeRate: number) => number;
  usdcValue: number;
};

const USDCSymbol = tokenConstants.USDC;
const USDSymbol = '$';

export type CoinDisplayTypes = 'USDC' | 'USD';

const displayPreferences = (
  comparisionWithUsdcValue: number,
  symbol: string,
  usdcValue: number,
): CoinDisplayPreferences => {
  const convertToDisplay = (usdcAmount: number) => Math.ceil(usdcAmount * comparisionWithUsdcValue);
  const convertFromDisplay = (usdAmount: number) => Math.ceil(usdAmount / comparisionWithUsdcValue);

  return {
    symbol,
    displayAmount: (usdcAmount: number) => convertToDisplay(usdcAmount),
    numericDisplayAmountToUsdc: (displayAmount: number) => convertFromDisplay(displayAmount),
    usdcToNumericDisplayAmount: (usdcAmount: number) => convertToDisplay(usdcAmount),
    usdcToNumericDisplayAmountWithUsdExchangeRate: (usdcAmount: number, exchangeRate: number) =>
      symbol === USDSymbol ? usdcAmount * exchangeRate : usdcAmount,
    usdcValue,
  };
};

export const useCoinDisplayTypes = () => {
  const {
    accountResult: {address: account},
  } = useContext(WalletContext);

  const {data: userProfile, isLoading} = useUserProfile({account});

  return useMemo(() => {
    return {
      coinDisplayTypes: !account || isLoading || userProfile?.displayInFiat ? tokenConstants.USD : tokenConstants.USDC,
    };
  }, [account, isLoading, userProfile?.displayInFiat]);
};

interface CoinDisplayPreferencesContextValue {
  coinDisplayPreferences: CoinDisplayPreferences;
}

const initialValue: CoinDisplayPreferencesContextValue = {} as CoinDisplayPreferencesContextValue;
const CoinDisplayPreferencesContext = createContext<CoinDisplayPreferencesContextValue>(initialValue);

export const CoinDisplayPreferencesProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const marketData = useExchangeRates();

  const {coinDisplayTypes} = useCoinDisplayTypes();

  const coinDisplayPreferences = useMemo(() => {
    const usdcValue = marketData?.data?.usdcToUsd || 1;

    return coinDisplayTypes === tokenConstants.USD
      ? displayPreferences(usdcValue, USDSymbol, usdcValue)
      : displayPreferences(1, USDCSymbol, usdcValue);
  }, [marketData, coinDisplayTypes]);

  return (
    <CoinDisplayPreferencesContext.Provider value={{coinDisplayPreferences}}>
      {children}
    </CoinDisplayPreferencesContext.Provider>
  );
};

export const DefaultCoinDisplayPreferencesProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const marketData = useExchangeRates();

  const usdcValue = marketData?.data?.usdcToUsd || 1;

  const coinDisplayPreferences = displayPreferences(usdcValue, USDSymbol, usdcValue);

  return (
    <CoinDisplayPreferencesContext.Provider value={{coinDisplayPreferences}}>
      {children}
    </CoinDisplayPreferencesContext.Provider>
  );
};

export const useCoinDisplayPreferences = () => {
  const {coinDisplayPreferences} = useContext(CoinDisplayPreferencesContext);
  return coinDisplayPreferences;
};
